/**
 * Custom Variant Form Handler
 * Handles variant picker updates for custom product forms (not using Horizon's ProductForm)
 * Also syncs variant selections with quantity breaks component
 */

import { ThemeEvents } from '@theme/events';

class VariantFormHandler {
  constructor() {
    this.init();
  }

  init() {
    // Listen for both variant selected (immediate) and variant update (after processing) events
    document.addEventListener(ThemeEvents.variantSelected, this.handleVariantSelected.bind(this));
    document.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate.bind(this));
    
    // Also initialize on page load to set correct initial variant
    document.addEventListener('DOMContentLoaded', this.handlePageLoad.bind(this));
    
    // If DOMContentLoaded already fired, run initialization immediately
    if (document.readyState === 'loading') {
      // DOMContentLoaded will fire
    } else {
      // DOM is already loaded
      this.handlePageLoad();
    }
  }

  /**
   * Handle initial page load to set correct variant
   */
  handlePageLoad() {
    console.log('Variant form handler: Initializing on page load');
    
    // Find all variant pickers and set initial state
    const variantPickers = document.querySelectorAll('variant-picker');
    variantPickers.forEach(picker => {
      this.initializeVariantPicker(picker);
    });
    
    const selected = document.querySelector(
      'variant-picker [data-variant-id][checked], variant-picker [data-variant-id][selected]'
    );
    if (selected) {
      const url = new URL(location);
      if (!url.searchParams.has('variant')) {
        url.searchParams.set('variant', selected.dataset.variantId);
        history.replaceState({}, '', url);
      }
    }
  }

  /**
   * Initialize a variant picker to set correct initial variant
   * @param {HTMLElement} variantPicker - The variant picker element
   */
  initializeVariantPicker(variantPicker) {
    if (!variantPicker || !variantPicker.dataset) {
      console.warn('Invalid variant picker or missing dataset');
      return;
    }
    
    const productId = variantPicker.dataset.productId;
    if (!productId) return;

    // Get the current URL variant parameter
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    
    // Find the form for this product
    const form = document.getElementById(`AddToCartForm--${productId}`);
    if (!form) return;

    const variantIdInput = form.querySelector('input[name="id"]');
    if (!variantIdInput) return;

    // If URL has a variant parameter, use that; otherwise use the current form value
    const initialVariantId = urlVariantId || variantIdInput.value;
    
    if (initialVariantId && initialVariantId !== variantIdInput.value) {
      console.log(`Setting initial variant ID to: ${initialVariantId}`);
      this.updateMainFormVariantId(form, initialVariantId, productId);
    }
    
    console.log(`Variant picker initialized for product ${productId} with variant ${initialVariantId}`);
  }

  /**
   * Handle immediate variant selection events (when user clicks)
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  getCartButton(productId) {
  return document.querySelector(`#AddToCartForm--${productId} .main-product__cart-btn`);
  }
  
  handleVariantSelected(event) {
    console.log('Variant selected event received:', event.detail);
    console.log('Event target:', event.target);
    
    // Get the variant picker that triggered this event
    const variantPicker = event.target;
    
    if (!variantPicker || !variantPicker.dataset || !variantPicker.dataset.productId) {
      console.warn('No variant picker, dataset, or product ID found', {
        variantPicker: variantPicker,
        hasDataset: variantPicker?.dataset,
        productId: variantPicker?.dataset?.productId
      });
      return;
    }
    
    const productId = variantPicker.dataset.productId;
    const mainForm = document.getElementById(`AddToCartForm--${productId}`);
    if (mainForm) {
      const mainVariantPicker = mainForm.closest('[data-product-id]')?.querySelector('variant-picker')
      if (mainVariantPicker) {
        const swatches = mainVariantPicker.querySelectorAll('input[type="radio"]');
        swatches.forEach((swatch) => {
          if (swatch instanceof HTMLInputElement || swatch instanceof HTMLSelectElement) {
            swatch.disabled = true;
          }
          swatch.closest('label')?.classList.add('is-disabled');
        });
      }
      const cartBtn = this.getCartButton(productId);
      if (cartBtn) {
        cartBtn.classList.add('si-cart-disabled');
        cartBtn.setAttribute("disabled", "disabled");
      }
    }
    const optionValueId = event.detail.resource.id;
    
    console.log(`Looking for option with data-option-value-id="${optionValueId}"`);
    
    // Find the selected option element to get variant ID
    const selectedOption = variantPicker.querySelector(`[data-option-value-id="${optionValueId}"]`);
    if (!selectedOption) {
      console.warn(`Could not find selected option with ID: ${optionValueId}`);
      // Try alternative selectors
      const allOptions = variantPicker.querySelectorAll('[data-option-value-id]');
      console.log('Available options:', Array.from(allOptions).map(opt => ({
        id: opt.dataset.optionValueId,
        variantId: opt.dataset.variantId,
        element: opt
      })));
      return;
    }
    
    if (!selectedOption.dataset) {
      console.warn('Selected option has no dataset:', selectedOption);
      return;
    }
    
    const variantId = selectedOption.dataset.variantId;
    if (!variantId) {
      console.warn('Selected option has no variant ID:', selectedOption);
      return;
    }
    
    // Update the form immediately
    const form = document.getElementById(`AddToCartForm--${productId}`);
    if (form) {
      console.log(`Variant selected: Updating form to variant ${variantId}`);
      this.updateMainFormVariantId(form, variantId, productId);
      
      // Clear quantity break selections when variant picker changes
      this.clearQuantityBreakSelections(productId);
      
      // Also sync other components immediately
      this.syncQuantityBreaksWithVariantPicker(productId, {}, { id: variantId });
      this.syncSellingPlanWithVariantPicker(productId, { id: variantId });
    } else {
      console.warn(`Could not find form for product ${productId}`);
    }
  }

  /**
   * Handle variant update events from the variant picker
   * @param {CustomEvent} event - The variant update event
   */
async handleVariantUpdate(event) {
  const variantId = event.detail.resource.id;
  const productId = event.detail.productId;
  const pdpData = event.target.closest(".js-pdp-data");
  let handle = pdpData?.dataset?.productHandle;
  let sectionId = pdpData?.dataset?.sectionId;

  if (!handle || !sectionId) {
    console.warn("Product handle or section ID not found");
    return;
  }

  const rootUrl = window.Shopify?.routes?.root || '/';
  const url = `${rootUrl}products/${handle}?variant=${variantId}&section_id=${sectionId}`;
  const res = await fetch(url);
  const html = await res.text();

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const updatedInner = doc.querySelector(".js-pdp-data-wrapper");
  if (updatedInner) {
    const currentWrapper = document.querySelector(".js-pdp-data-wrapper");
    if (currentWrapper) {
      const preserveBlocks = [
        { selector: '.pdp-frequently-bought-together', mode: 'clone' },
        { selector: '[data-fbt-block]', mode: 'clone' },
        { selector: '.sticky-atc', mode: 'live' }
      ];
      
      const preservedBlocks = [];
      preserveBlocks.forEach(({ selector, mode }) => {
        const block = currentWrapper.querySelector(selector);
        if (!block) return;
        
        const element = mode === 'live' ? block : block.cloneNode(true);

        // Save scroll positions of scrollable children before detaching
        const savedScrollPositions = [];
        if (mode === 'live') {
          element.querySelectorAll('*').forEach(child => {
            if (child.scrollLeft > 0 || child.scrollTop > 0) {
              savedScrollPositions.push({ el: child, left: child.scrollLeft, top: child.scrollTop });
            }
          });
          block.parentElement?.removeChild(block);
        }

        preservedBlocks.push({ element, selector, savedScrollPositions });
      });
      
      currentWrapper.innerHTML = updatedInner.innerHTML;
      
      preservedBlocks.forEach(({element, selector, savedScrollPositions}) => {
        element.style.setProperty('transition', 'none', 'important');

        const targetPosition = currentWrapper.querySelector(selector);
        if (targetPosition) {
          targetPosition.replaceWith(element);
        } else {
          currentWrapper.appendChild(element);
        }

        // Restore scroll positions instantly after re-insertion
        if (savedScrollPositions && savedScrollPositions.length) {
          savedScrollPositions.forEach(({ el, left, top }) => {
            el.scrollLeft = left;
            el.scrollTop = top;
          });
        }

        void element.offsetHeight;
        element.style.removeProperty('transition');
      });
    }
    
    document.dispatchEvent(new CustomEvent('variant:loaded'));
  }

  const { variant, selectedOptions } = event.detail;

  if (!variant) {
    console.warn("No variant data in update event");
    return;
  }

  const form = document.getElementById(`AddToCartForm--${productId}`);
  if (!form) {
    console.warn(`Could not find form for product ${productId}`);
    return;
  }

  this.updateMainFormVariantId(form, variant.id, productId);
  this.clearQuantityBreakSelections(productId);
  this.updateFormAvailability(form, variant);
  this.updatePriceDisplay(productId, variant);
  this.syncQuantityBreaksWithVariantPicker(productId, selectedOptions, variant);
  this.syncSellingPlanWithVariantPicker(productId, variant);

  setTimeout(() => {
    this.ensureVariantPickerPriority(form, variant.id, productId);
  }, 10);

  // Enable all swatches
  const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
  if (variantPicker) {
    const allSwatches = variantPicker.querySelectorAll('input[type="radio"]');
    allSwatches.forEach((swatch) => {
      swatch.disabled = false;
      swatch.closest("label")?.classList.remove("is-disabled");
    });
  }
  const cartBtn = this.getCartButton(productId);
  if (cartBtn) {
    cartBtn.classList.remove('si-cart-disabled');
    cartBtn.removeAttribute("disabled");
  }
}


  /**
   * Update the main form variant ID with logging
   * @param {HTMLFormElement} form - The product form
   * @param {string} variantId - The variant ID
   * @param {string} productId - The product ID
   */
  updateMainFormVariantId(form, variantId, productId) {
    const variantIdInput = form.querySelector('input[name="id"]');
    if (variantIdInput) {
      const oldValue = variantIdInput.value;
      variantIdInput.value = variantId;
      console.log(`Updated main form variant ID from ${oldValue} to: ${variantId}`);
      
      // Add a data attribute to track that this was set by variant picker
      variantIdInput.setAttribute('data-variant-picker-set', 'true');
      variantIdInput.setAttribute('data-variant-picker-timestamp', Date.now().toString());
    }
  }

  /**
   * Ensure variant picker selection takes priority over other components
   * @param {HTMLFormElement} form - The product form
   * @param {string} variantId - The variant ID
   * @param {string} productId - The product ID
   */
  ensureVariantPickerPriority(form, variantId, productId) {
    const variantIdInput = form.querySelector('input[name="id"]');
    if (variantIdInput) {
      const currentValue = variantIdInput.value;
      const wasSetByVariantPicker = variantIdInput.getAttribute('data-variant-picker-set') === 'true';
      
      if (currentValue !== variantId) {
        console.warn(`Variant ID was changed from ${variantId} to ${currentValue} - restoring variant picker selection`);
        variantIdInput.value = variantId;
        variantIdInput.setAttribute('data-variant-picker-set', 'true');
        variantIdInput.setAttribute('data-variant-picker-timestamp', Date.now().toString());
      } else if (wasSetByVariantPicker) {
        console.log(`Variant picker selection maintained: ${variantId}`);
      }
    }
  }

  /**
   * Clear all quantity break selections when variant picker changes
   * @param {string} productId - The main product ID
   */
  clearQuantityBreakSelections(productId) {
    const quantityBreaksComponent = document.querySelector('pdp-quantity-breaks');
    if (!quantityBreaksComponent) {
      console.log('No quantity breaks component found to clear');
      return;
    }

    console.log('Clearing quantity break selections due to variant picker change');

    // Find all quantity break radio buttons
    const quantityBreakRadios = quantityBreaksComponent.querySelectorAll('.js-pdp-qty-breaks');
    
    quantityBreakRadios.forEach(radio => {
      if (radio.checked) {
        console.log(`Unchecking quantity break radio: ${radio.id}`);
        radio.checked = false;
        
        // Update tabindex for proper keyboard navigation
        radio.setAttribute('tabindex', '-1');
        
        // Remove visual selected state from label
        const label = radio.closest('label');
        if (label) {
          label.classList.remove('selected');
        }
      }
    });

    // Update ARIA states and visual states through the component if available
    if (quantityBreaksComponent.updateAllVisualStates) {
      quantityBreaksComponent.updateAllVisualStates();
    }
    
    if (quantityBreaksComponent.updateAriaStates) {
      quantityBreaksComponent.updateAriaStates();
    }

    // Clear any live region announcements
    const liveRegion = quantityBreaksComponent.querySelector('[aria-live]');
    if (liveRegion) {
      liveRegion.textContent = '';
      
      // Announce the clearing to screen readers after a brief delay
      setTimeout(() => {
        liveRegion.textContent = 'Quantity break selections cleared due to variant change';
      }, 100);
    }

    // Ensure the main form uses the variant picker's selection, not quantity breaks
    const form = document.getElementById(`AddToCartForm--${productId}`);
    if (form) {
      const variantIdInput = form.querySelector('input[name="id"]');
      if (variantIdInput && variantIdInput.getAttribute('data-variant-picker-set') === 'true') {
        console.log(`Maintaining variant picker selection: ${variantIdInput.value}`);
        // The variant picker selection should remain as the form value
      }
    }

    console.log('Quantity break selections cleared');
  }

  /**
   * Sync quantity breaks variant selectors with main variant picker selections
   * @param {string} productId - The main product ID
   * @param {Object} selectedOptions - The selected variant options from the main picker
   * @param {Object} mainVariant - The selected variant from the main picker
   */
  syncQuantityBreaksWithVariantPicker(productId, selectedOptions, mainVariant) {
    const quantityBreaksComponent = document.querySelector('pdp-quantity-breaks');
    if (!quantityBreaksComponent) {
      console.log('No quantity breaks component found');
      return;
    }

    console.log('Syncing quantity breaks with variant picker:', selectedOptions);

    // Find all variant selectors in quantity breaks
    const variantSelectors = quantityBreaksComponent.querySelectorAll('.js-variant-select');
    
    variantSelectors.forEach(selector => {
      if (!selector.dataset) {
        console.warn('Variant selector missing dataset:', selector);
        return;
      }
      
      const productIdFromSelector = selector.dataset.productId;
      const radioId = selector.dataset.radioId;
      const radio = quantityBreaksComponent.querySelector(`#${radioId}`);
      
      if (!radio) return;

      console.log(`Processing quantity break for product: ${productIdFromSelector}`);

      // Find the best matching variant for this product based on the main picker's selections
      // If selector is not a <select> (our UI uses option radios), try to use the hidden select inside that quantity-break label
      let selectorForMatching = selector;
      if (selector.tagName !== 'SELECT') {
        const labelEl = radio.closest('label');
        const hiddenSelect = labelEl ? labelEl.querySelector('.js-hidden-variant-select') : null;
        if (hiddenSelect) selectorForMatching = hiddenSelect;
      }

      const matchingVariant = this.findMatchingVariantForQuantityBreak(
        selectorForMatching,
        selectedOptions,
        productIdFromSelector,
        mainVariant
      );

      if (matchingVariant) {
        // Update internal UI elements depending on type
        if (selector.tagName === 'SELECT') {
          selector.value = matchingVariant.variantId;
        } else {
          // For radio-based UI, update the checked inputs to reflect the variant
          const labelEl = radio.closest('label');
          if (labelEl) {
            const optionGroups = Array.from(labelEl.querySelectorAll('.variant-option'));
            const optionVals = (matchingVariant.title || '').split('/').map(s => s.trim());
            // For each group, try to find a matching option by text and check it
            optionGroups.forEach((group, idx) => {
              const targetText = optionVals[idx];
              let found = null;
              group.querySelectorAll('label').forEach(lbl => {
                const textEl = lbl.querySelector('.variant-option__button-label__text');
                if (textEl && textEl.textContent.trim().toLowerCase() === (targetText || '').toLowerCase()) {
                  found = lbl.querySelector('input.js-variant-select');
                }
              });
              if (found) {
                found.checked = true;
              }
            });
          }
        }
        
        // Update the radio button value
        radio.value = matchingVariant.variantId;
        
        // Update pricing display
        this.updateQuantityBreakPricing(radio, matchingVariant);
        
        // If this quantity break is currently selected, update the main form
        if (radio.checked) {
          const mainFormInput = document.querySelector(`#AddToCartForm--${productId} input[name="id"]`);
          if (mainFormInput) {
            mainFormInput.value = matchingVariant.variantId;
          }
        }

        console.log(`Updated quantity break variant for product ${productIdFromSelector} to variant ${matchingVariant.variantId}`);
      }
    });
  }

  /**
   * Find the best matching variant for a quantity break product based on main picker selections
   * @param {HTMLSelectElement} selector - The variant selector element
   * @param {Object} selectedOptions - Selected options from main variant picker
   * @param {string} productId - The quantity break product ID
   * @param {Object} mainVariant - The main product variant
   * @returns {Object|null} - The matching variant info or null
   */
  findMatchingVariantForQuantityBreak(selector, selectedOptions, productId, mainVariant) {
    const options = Array.from(selector.options);
    
    // If no options or only one option, return the first available one
    if (options.length <= 1) {
      return options.length === 1 ? {
        variantId: options[0].value,
        price: options[0].dataset.price,
        comparePrice: options[0].dataset.comparePrice
      } : null;
    }

    // Try to find exact matches first
    let bestMatch = null;
    let bestMatchScore = -1;

    options.forEach(option => {
      if (option.disabled) return; // Skip unavailable variants
      
      const variantTitle = option.textContent.trim();
      let matchScore = 0;
      
      // Score based on how many selected options match this variant's title
      if (selectedOptions) {
        Object.values(selectedOptions).forEach(selectedValue => {
          if (selectedValue && variantTitle.toLowerCase().includes(selectedValue.toLowerCase())) {
            matchScore++;
          }
        });
      }
      
      // Prefer available variants
      if (!option.disabled) {
        matchScore += 0.5;
      }
      
      if (matchScore > bestMatchScore) {
        bestMatchScore = matchScore;
        bestMatch = {
          variantId: option.value,
          price: option.dataset.price,
          comparePrice: option.dataset.comparePrice,
          title: variantTitle
        };
      }
    });

    // If no good match found, return the first available option
    if (!bestMatch) {
      const firstAvailable = options.find(option => !option.disabled);
      if (firstAvailable) {
        bestMatch = {
          variantId: firstAvailable.value,
          price: firstAvailable.dataset.price,
          comparePrice: firstAvailable.dataset.comparePrice,
          title: firstAvailable.textContent.trim()
        };
      }
    }

    return bestMatch;
  }

  /**
   * Update pricing display for a quantity break
   * @param {HTMLInputElement} radio - The radio button element
   * @param {Object} variantInfo - The variant information
   */
  updateQuantityBreakPricing(radio, variantInfo) {
    const label = radio.closest('label');
    if (!label) return;

    const priceDisplay = label.querySelector('.js-price-display');
    const comparePriceDisplay = label.querySelector('.js-compare-price-display');
    
    if (priceDisplay && variantInfo.price) {
      const price = parseInt(variantInfo.price);
      const comparePrice = variantInfo.comparePrice ? parseInt(variantInfo.comparePrice) : null;
      
      // Format price using basic money format
      const formattedPrice = this.formatMoney(price);
      priceDisplay.textContent = `Sale Price: ${formattedPrice}`;
      
      // Handle compare price
      if (comparePriceDisplay) {
        if (comparePrice && comparePrice > price) {
          const formattedComparePrice = this.formatMoney(comparePrice);
          comparePriceDisplay.textContent = formattedComparePrice;
          comparePriceDisplay.style.display = '';
          comparePriceDisplay.setAttribute('aria-label', `Regular price: ${formattedComparePrice}`);
        } else {
          comparePriceDisplay.style.display = 'none';
        }
      }
    }
  }

  /**
   * Sync selling plan component with main variant picker selection
   * @param {string} productId - The main product ID
   * @param {Object} variant - The selected variant from the main picker
   */
  syncSellingPlanWithVariantPicker(productId, variant) {
    const sellingPlanComponent = document.querySelector('pdp-selling-plan');
    if (!sellingPlanComponent) {
      console.log('No selling plan component found');
      return;
    }

    console.log(`Syncing selling plan with variant picker - variant ID: ${variant.id}`);

    // Update the selling plan component's variant ID
    if (sellingPlanComponent.updateProductVariant) {
      sellingPlanComponent.updateProductVariant(variant.id);
    } else {
      // Fallback: update data attribute directly
      sellingPlanComponent.dataset.productVariantId = variant.id;
    }

    // Dispatch a custom event to notify the selling plan component
    const variantUpdateEvent = new CustomEvent('variant-picker-changed', {
      detail: {
        productId: productId,
        variantId: variant.id,
        variant: variant
      },
      bubbles: true
    });
    
    sellingPlanComponent.dispatchEvent(variantUpdateEvent);
    
    console.log(`Updated selling plan variant ID to: ${variant.id}`);
  }

  /**
   * Basic money formatting
   * @param {number} cents - Price in cents
   * @returns {string} - Formatted price
   */
  formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  /**
   * Update form availability based on variant
   * @param {HTMLFormElement} form - The product form
   * @param {Object} variant - The selected variant
   */
  updateFormAvailability(form, variant) {
    const submitButton = form.querySelector('button[type="submit"]');
    
    if (variant.available) {
      form.removeAttribute('disabled');
      if (submitButton) {
        submitButton.disabled = false;
        this.updateButtonText(submitButton, submitButton.dataset.addToCartText || 'Add to Cart', variant);
      }
    } else {
      form.setAttribute('disabled', '');
      if (submitButton) {
        submitButton.disabled = true;
        this.updateButtonText(submitButton, submitButton.dataset.soldOutText || 'Sold Out', null);
      }
    }
  }

  /**
   * Update button text while preserving price span
   * @param {HTMLButtonElement} button - The submit button
   * @param {string} text - The text to set
   * @param {Object} variant - The variant object (for price)
   */
  updateButtonText(button, text, variant) {
    if (button.tagName === 'INPUT') {
      button.value = text;
      return;
    }

    // plp-card span structure: .plp-card__button-text + optional .plp-card__button-price
    const plpTextSpan = button.querySelector('.plp-card__button-text');
    const plpPriceSpan = button.querySelector('.plp-card__button-price');

    if (plpTextSpan) {
      plpTextSpan.textContent = text;
      if (plpPriceSpan && variant) {
        plpPriceSpan.textContent = this.formatMoney(variant.price);
      }
      return;
    }

    // Generic .button__price span structure
    const priceSpan = button.querySelector('.button__price');

    if (priceSpan && variant) {
      const formattedPrice = this.formatMoney(variant.price);
      priceSpan.textContent = `(${formattedPrice})`;

      const textNode = document.createTextNode(text + ' ');
      button.innerHTML = '';
      button.appendChild(textNode);
      button.appendChild(priceSpan);
    } else {
      button.textContent = text;
    }
  }

  /**
   * Format money using Shopify's money format
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price
   */
  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    // Fallback formatting
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Update price display when variant changes
   * @param {string} productId - The product ID
   * @param {Object} variant - The selected variant
   */
  updatePriceDisplay(productId, variant) {
    const priceElement = document.querySelector(`[data-product-id="${productId}"] .price`);
    if (priceElement && variant.price !== undefined) {
      // Update price display logic here if needed
      // This would depend on your existing price display implementation
    }
  }
}

// Initialize the variant form handler
new VariantFormHandler();

export default VariantFormHandler;
